import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Place as PlaceIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';
import CollectionSiteCard from '../../components/containers/CollectionSiteCard';
import CollectionSiteViewDialog from '../../components/containers/CollectionSiteViewDialog';
import {
  CollectionSiteFormDialog,
  CollectionSiteFormState,
} from '../../components/containers/CollectionSiteFormDialog';
import { CollectionSiteDeleteDialog } from '../../components/containers/CollectionSiteDeleteDialog';
import { useCollectionSiteStore } from '../../stores/collection-site.store';
import { useOrganizationStore } from '../../stores/organization.store';
import { CollectionSiteType, CollectionSiteSchema } from '../../types/collection-site.type';
import { isValidEmail } from '../../helpers/validation.helper';

function parseCoordinate(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? undefined : parsed;
}

const CollectionSites: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { sites, isLoading, getCollectionSites, createCollectionSite, updateCollectionSite, deleteCollectionSite } = useCollectionSiteStore();
  const { selectedOrganization } = useOrganizationStore();
  const [page] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<CollectionSiteType | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState<CollectionSiteSchema | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CollectionSiteSchema | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [viewSite, setViewSite] = useState<CollectionSiteSchema | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [dismissAddHint, setDismissAddHint] = useState(false);
  /** Knowledge Base "Go to Add collection site" guided tips. */
  type HelpAddPhase = 'addButton' | 'form' | 'submit';
  const [helpAddPhase, setHelpAddPhase] = useState<HelpAddPhase | null>(null);
  /** Knowledge Base "Go to Edit or delete collection site" tips. */
  type HelpRowActionsPhase = 'edit' | 'delete';
  const [helpRowActionsPhase, setHelpRowActionsPhase] =
    useState<HelpRowActionsPhase | null>(null);
  const {
    isStep,
    tourPhase,
    completeStep,
    dismissTour,
    setTourPhase,
    advanceTourPhase,
  } = useSelfSetupTour();
  const tourFiltersHint = isStep('sites') && tourPhase === 'filters';
  const tourTableHint = isStep('sites') && tourPhase === 'table';
  const tourAddHint = isStep('sites') && tourPhase === 'addButton';
  const tourFormHint =
    (isStep('sites') && tourPhase === 'form') || helpAddPhase === 'form';
  const tourSubmitHint =
    (isStep('sites') && tourPhase === 'submit') || helpAddPhase === 'submit';
  const tourDialogOpen = tourFormHint || tourSubmitHint;

  useEffect(() => {
    if (selectedOrganization) {
      loadSites();
    }
  }, [selectedOrganization, page, limit, search]);

  const loadSites = async () => {
    if (selectedOrganization) {
      await getCollectionSites({
        page,
        limit,
        organization_id: selectedOrganization.id,
        search: search || undefined,
      });
    }
  };

  const [formData, setFormData] = useState<CollectionSiteFormState>({
    longitude: '',
    latitude: '',
    google_maps_url: '',
    area: '',
    data: {
      name: '',
      type: CollectionSiteType.RESIDENTIAL,
      contact_info: {
        name: '',
        phone: '',
        email: '',
      },
      schedule: {
        pickup_days: [] as string[],
        pickup_time: '',
      },
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
      },
      notes: '',
      capacity: '',
      operating_hours: '',
      special_instructions: '',
    },
    status: '',
  });

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.data.name.trim()) {
      nextErrors.name = t('collectionSites.errors.nameRequired');
    }

    if (!formData.latitude.trim() || parseCoordinate(formData.latitude) == null) {
      nextErrors.latitude = t('collectionSites.errors.latitudeRequired');
    }

    if (!formData.longitude.trim() || parseCoordinate(formData.longitude) == null) {
      nextErrors.longitude = t('collectionSites.errors.longitudeRequired');
    }

    if (!formData.data.contact_info.name.trim()) {
      nextErrors.contactName = t('collectionSites.errors.contactNameRequired');
    }

    if (!formData.data.contact_info.phone.trim()) {
      nextErrors.contactPhone = t('collectionSites.errors.contactPhoneRequired');
    }

    const contactEmail = formData.data.contact_info.email.trim();
    if (!contactEmail) {
      nextErrors.contactEmail = t('collectionSites.errors.contactEmailRequired');
    } else if (!isValidEmail(contactEmail)) {
      nextErrors.contactEmail = t('collectionSites.errors.contactEmailInvalid');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreateSite = async () => {
    if (!selectedOrganization || !validateForm()) return;

    await createCollectionSite({
      ...formData,
      latitude: Number(formData.latitude),
      longitude: Number(formData.longitude),
      area: formData.area ? Number(formData.area) : undefined,
      organization_id: selectedOrganization.id,
    });
    setShowModal(false);
    resetForm();
    setErrors({});
  };

  const handleUpdateSite = async () => {
    if (!selectedSite || !validateForm()) return;

    await updateCollectionSite(selectedSite.id, {
      ...formData,
      latitude: Number(formData.latitude),
      longitude: Number(formData.longitude),
      area: formData.area ? Number(formData.area) : undefined,
      organization_id: selectedOrganization?.id,
    });
    setShowModal(false);
    setSelectedSite(null);
    setErrors({});
  };

  const handleDeleteSite = (siteId: string) => {
    const site = sites?.find((item) => item.id === siteId);
    if (site) {
      setDeleteTarget(site);
    }
  };

  const handleConfirmDeleteSite = async () => {
    if (!deleteTarget || deleteSubmitting) return;

    setDeleteSubmitting(true);
    try {
      const success = await deleteCollectionSite(deleteTarget.id);
      if (success) {
        setDeleteTarget(null);
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleOpenView = (site: CollectionSiteSchema) => {
    setViewSite(site);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewSite(null);
  };

  const handleOpenEdit = (site: CollectionSiteSchema) => {
    setSelectedSite(site);
    setFormData({
      longitude: site.longitude != null ? String(site.longitude) : '',
      latitude: site.latitude != null ? String(site.latitude) : '',
      google_maps_url: site.google_maps_url ?? '',
      area: site.area != null ? String(site.area) : '',
      data: {
        name: site.data?.name || '',
        type: site.data?.type || CollectionSiteType.RESIDENTIAL,
        contact_info: site.data?.contact_info || { name: '', phone: '', email: '' },
        schedule: site.data?.schedule || { pickup_days: [], pickup_time: '' },
        address: site.data?.address || { street: '', city: '', state: '', country: '', postal_code: '' },
        notes: site.data?.notes || '',
        capacity: site.data?.capacity || '',
        operating_hours: site.data?.operating_hours || '',
        special_instructions: site.data?.special_instructions || '',
      },
      status: site.status ?? '',
    });
    setErrors({});
    setShowModal(true);
  };

  const filteredSites = useMemo(() => {
    const list = sites ?? [];
    if (!typeFilter) return list;
    return list.filter((site) => site.data?.type === typeFilter);
  }, [sites, typeFilter]);

  const resetForm = () => {
    setFormData({
      longitude: '',
      latitude: '',
      google_maps_url: '',
      area: '',
      data: {
        name: '',
        type: CollectionSiteType.RESIDENTIAL,
        contact_info: {
          name: '',
          phone: '',
          email: '',
        },
        schedule: {
          pickup_days: [],
          pickup_time: '',
        },
        address: {
          street: '',
          city: '',
          state: '',
          country: '',
          postal_code: '',
        },
        notes: '',
        capacity: '',
        operating_hours: '',
        special_instructions: '',
      },
      status: '',
    });
  };

  useEffect(() => {
    const state = location.state as {
      openAdd?: boolean;
      showAddHint?: boolean;
      showRowActionsHint?: boolean;
    } | null;
    if (!state?.openAdd && !state?.showAddHint && !state?.showRowActionsHint) return;
    if (state.openAdd) {
      setSelectedSite(null);
      resetForm();
      setErrors({});
      setShowModal(true);
    }
    if (state.showAddHint) {
      setHelpAddPhase('addButton');
      setDismissAddHint(false);
    }
    if (state.showRowActionsHint) {
      setSearch('');
      setTypeFilter('');
      setHelpRowActionsPhase('edit');
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const finishHelpAddGuide = () => {
    setHelpAddPhase(null);
    setShowModal(false);
    setSelectedSite(null);
  };

  const handlePasteLatLng = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const cleaned = text.trim().replace(/[()]/g, ''); // optional

      const [lat, lng] = cleaned.split(',').map((v) => v.trim());

      if (!lat || !lng) {
        alert(t('collectionSites.clipboardFormatError'));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));
    } catch (err) {
      alert(t('collectionSites.clipboardReadError'));
    }
  };


  // Self-setup: open create dialog once when entering form/submit tip phases.
  useEffect(() => {
    if (!tourDialogOpen) return;
    if (showModal || selectedSite) return;
    setSelectedSite(null);
    resetForm();
    setErrors({});
    setShowModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when entering dialog tip phases
  }, [tourDialogOpen]);

  // Only from Help deep-link or setup dialog — not auto on empty list.
  const showAddHint =
    !dismissAddHint &&
    !isLoading &&
    !showModal &&
    (tourAddHint ||
      (helpAddPhase === 'addButton' &&
        !(
          isStep('sites') &&
          (tourPhase === 'nav' ||
            tourPhase === 'filters' ||
            tourPhase === 'table' ||
            tourPhase === 'form' ||
            tourPhase === 'submit')
        )));

  if (isLoading) {
    return <LoadingSkeleton variant="cards" filters={3} cardCount={8} />;
  }

  return (
    <Box>
      <PageHeader
        icon={<PlaceIcon />}
        title={t('collectionSites.title')}
        actions={
          <SetupCoachmark
            open={showAddHint}
            title={t('collectionSites.addSiteHintTitle')}
            subtitle={t('collectionSites.addSiteHint')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              setDismissAddHint(true);
              if (tourAddHint) {
                advanceTourPhase();
                return;
              }
              if (helpAddPhase === 'addButton') {
                setHelpAddPhase('form');
                setSelectedSite(null);
                resetForm();
                setErrors({});
                setShowModal(true);
              }
            }}
            onDismiss={() => {
              setDismissAddHint(true);
              if (tourAddHint) dismissTour();
              else if (helpAddPhase === 'addButton') setHelpAddPhase(null);
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDismissAddHint(true);
                setSelectedSite(null);
                resetForm();
                setErrors({});
                setShowModal(true);
                if (tourAddHint) setTourPhase('form');
                else if (helpAddPhase === 'addButton') setHelpAddPhase('form');
              }}
            >
              {t('collectionSites.addSite')}
            </Button>
          </SetupCoachmark>
        }
      />

      <SetupCoachmark
        open={tourFiltersHint}
        fullWidth
        title={t('selfSetup.tour.sitesFiltersTitle')}
        subtitle={t('selfSetup.tour.sitesFiltersSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Card sx={{ mb: 3 }} elevation={0} variant="outlined">
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('collectionSites.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('common.type')}</InputLabel>
                  <Select
                    value={typeFilter}
                    label={t('common.type')}
                    onChange={(e) => setTypeFilter(e.target.value as CollectionSiteType | '')}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">{t('collectionSites.allTypes')}</MenuItem>
                    {Object.values(CollectionSiteType).map((siteType) => (
                      <MenuItem key={siteType} value={siteType}>
                        {t(`collectionSites.types.${siteType}`, { defaultValue: siteType })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </SetupCoachmark>

      <SetupCoachmark
        open={tourTableHint}
        fullWidth
        title={t('selfSetup.tour.sitesTableTitle')}
        subtitle={t('selfSetup.tour.sitesTableSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Grid container spacing={2}>
          {filteredSites.length > 0 ? (
            filteredSites.map((site, index) => (
              <Grid item xs={12} sm={6} md={4} xl={3} key={site.id}>
                <CollectionSiteCard
                  site={site}
                  onView={handleOpenView}
                  onEdit={handleOpenEdit}
                  onDelete={handleDeleteSite}
                  helpRowActionsPhase={
                    index === 0 ? helpRowActionsPhase : null
                  }
                  onHelpRowActionsNext={() => setHelpRowActionsPhase('delete')}
                  onHelpRowActionsDismiss={() => setHelpRowActionsPhase(null)}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <SetupCoachmark
                open={Boolean(helpRowActionsPhase)}
                fullWidth
                title={t('collectionSites.editDeleteSiteEmptyTitle')}
                subtitle={t('collectionSites.editDeleteSiteEmptyHint')}
                nextLabel={t('selfSetup.tourDone')}
                onNext={() => setHelpRowActionsPhase(null)}
                onDismiss={() => setHelpRowActionsPhase(null)}
              >
                <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
                  <CardContent sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">{t('collectionSites.emptyList')}</Typography>
                  </CardContent>
                </Card>
              </SetupCoachmark>
            </Grid>
          )}
        </Grid>
      </SetupCoachmark>

      <CollectionSiteViewDialog
        site={viewSite}
        open={viewOpen}
        onClose={handleCloseView}
        onEdit={handleOpenEdit}
      />

      <CollectionSiteFormDialog
        open={showModal}
        onClose={() => {
          if (helpAddPhase) {
            finishHelpAddGuide();
            return;
          }
          if (tourFormHint || tourSubmitHint) dismissTour();
          setShowModal(false);
        }}
        isEdit={Boolean(selectedSite)}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onPasteLatLng={handlePasteLatLng}
        onSubmit={selectedSite ? handleUpdateSite : handleCreateSite}
        tourFormHint={tourFormHint}
        tourSubmitHint={tourSubmitHint}
        onTourFormNext={() => {
          if (helpAddPhase === 'form') {
            setHelpAddPhase('submit');
            return;
          }
          advanceTourPhase();
        }}
        onTourSubmitDone={() => {
          if (helpAddPhase === 'submit') {
            finishHelpAddGuide();
            return;
          }
          completeStep('sites');
          setShowModal(false);
        }}
        onTourDismiss={() => {
          if (helpAddPhase) {
            finishHelpAddGuide();
            return;
          }
          dismissTour();
          setShowModal(false);
        }}
      />

      <CollectionSiteDeleteDialog
        open={!!deleteTarget}
        site={deleteTarget}
        loading={deleteSubmitting}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={handleConfirmDeleteSite}
      />
    </Box>
  );
};

export default CollectionSites; 
